import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/api/queryKeys';
import { STALE_TIME_SHORT, STALE_TIME_LONG } from '@/shared/constants/timing';
import * as api from '../api/organisationsApi';
import type { OrganisationFilters, Organisation } from '../types';

export function useOrganisations(filters: OrganisationFilters = {}) {
  return useQuery({
    queryKey: queryKeys.organisations.list(filters),
    queryFn: () => api.getOrganisations(filters),
    staleTime: STALE_TIME_LONG,
  });
}

export function useOrganisation(orgId: string) {
  return useQuery({
    queryKey: queryKeys.organisations.detail(orgId),
    queryFn: () => api.getOrganisation(orgId),
    enabled: !!orgId,
    staleTime: STALE_TIME_SHORT,
  });
}

export function useUpdateOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: Partial<Organisation> }) =>
      api.updateOrganisation(orgId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organisations.detail(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.organisations.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.search.organisations() });
    },
  });
}

export function useCreateOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      userId: string;
      companyName: string;
      companyType: 'licensee' | 'licensor' | 'retailer' | 'agent';
    }) => api.createOrganisation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organisations.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.validations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.search.organisations() });
    },
  });
}

// Employees hooks
export function useOrganisationEmployees(orgId: string) {
  return useQuery({
    queryKey: queryKeys.organisations.employees(orgId),
    queryFn: () => api.getOrganisationEmployees(orgId),
    enabled: !!orgId,
    staleTime: STALE_TIME_SHORT,
  });
}

export function useAddOrganisationEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: Parameters<typeof api.addOrganisationEmployee>[1] }) =>
      api.addOrganisationEmployee(orgId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organisations.employees(orgId) });
    },
  });
}

export function useRemoveOrganisationEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      api.removeOrganisationEmployee(orgId, userId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organisations.employees(orgId) });
    },
  });
}

// Brands hooks
export function useOrganisationBrands(orgId: string) {
  return useQuery({
    queryKey: queryKeys.organisations.brands(orgId),
    queryFn: () => api.getOrganisationBrands(orgId),
    enabled: !!orgId,
    staleTime: STALE_TIME_SHORT,
  });
}

export function useAddOrganisationBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: Parameters<typeof api.addOrganisationBrand>[1] }) =>
      api.addOrganisationBrand(orgId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organisations.brands(orgId) });
    },
  });
}

// Modules hooks
export function useOrganisationModules(orgId: string) {
  return useQuery({
    queryKey: queryKeys.organisations.modules(orgId),
    queryFn: () => api.getOrganisationModules(orgId),
    enabled: !!orgId,
    staleTime: STALE_TIME_SHORT,
  });
}

// Aliases hooks
export function useOrganisationAliases(orgId: string) {
  return useQuery({
    queryKey: queryKeys.organisations.aliases(orgId),
    queryFn: () => api.getOrganisationAliases(orgId),
    enabled: !!orgId,
    staleTime: STALE_TIME_SHORT,
  });
}

// Config hooks
export function useOrganisationConfig(orgId: string) {
  return useQuery({
    queryKey: queryKeys.organisations.config(orgId),
    queryFn: () => api.getOrganisationConfig(orgId),
    enabled: !!orgId,
    staleTime: STALE_TIME_SHORT,
  });
}

export function useUpdateOrganisationConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, configId, orgConfig }: { orgId: string; configId: string; orgConfig: Record<string, unknown> }) =>
      api.updateOrganisationConfig(orgId, configId, orgConfig),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organisations.config(orgId) });
    },
  });
}
